/**
 * Acha caminho de arquivo escrito no meio do texto da conversa.
 *
 * O Claude escreve caminho o tempo todo em prosa — "conserta o
 * `apps/web/src/api.ts`", "gravei em C:\\projetos\\notas.md" — e isso ficava
 * texto morto: para abrir, a pessoa tinha de achar o arquivo no explorador à
 * mão. Link em markdown já abria no editor; caminho solto, não.
 *
 * A regra é conservadora de propósito. Marcar demais é pior que marcar de
 * menos: uma frase salpicada de sublinhados que não levam a lugar nenhum
 * cansa mais do que ajuda, e cada falso positivo é um clique que dá erro.
 */

export interface Fatia {
  texto: string;
  /** Presente quando esta fatia é um caminho para abrir. */
  caminho?: string;
}

/**
 * Duas formas, e só duas:
 *
 * 1. caminho do Windows com letra de disco — `C:\algo` ou `C:/algo`, que não
 *    se confunde com nada;
 * 2. caminho com barra E extensão de arquivo — `docs/plano.md`,
 *    `apps/web/src/api.ts`. A extensão é o que separa caminho de frase com
 *    barra ("entrada/saída", "ele/ela").
 *
 * Fica de fora, de propósito: pasta relativa sem extensão (`src/componentes`),
 * porque "manda/recebe" viraria link; e caminho POSIX absoluto sem extensão
 * (`/home/claude/projetos`), porque em texto isso é quase sempre outra coisa.
 */
const CAMINHO =
  /(?:[A-Za-z]:[\\/][^\s"'`<>|*?]*[^\s"'`<>|*?.,;:!)\]}]|(?:\.{0,2}[\\/])?(?:[\w.@+-]+[\\/])+[\w.@+-]+\.[A-Za-z0-9]{1,6})/g;

/** Pontuação que costuma encostar no fim de um caminho dentro de uma frase. */
function aparar(bruto: string): string {
  return bruto.replace(/[.,;:!?)\]}]+$/, '');
}

/**
 * Quebra o texto em fatias, marcando as que são caminho.
 *
 * Devolve uma fatia só, sem `caminho`, quando não há nada a marcar — assim
 * quem chama pode comparar o tamanho e nem trocar o nó da árvore.
 */
export function fatiarCaminhos(texto: string): Fatia[] {
  const fatias: Fatia[] = [];
  let ultimo = 0;

  for (const achado of texto.matchAll(CAMINHO)) {
    const inicio = achado.index ?? 0;
    const bruto = achado[0];
    const limpo = aparar(bruto);
    if (!limpo) continue;

    // Um caminho tem de ter separador: `versao.1.2` casaria a segunda forma
    // sem ser caminho de nada.
    if (!/[\\/]/.test(limpo)) continue;

    if (inicio > ultimo) fatias.push({ texto: texto.slice(ultimo, inicio) });
    fatias.push({ texto: limpo, caminho: limpo });
    ultimo = inicio + limpo.length;
  }

  if (fatias.length === 0) return [{ texto }];
  if (ultimo < texto.length) fatias.push({ texto: texto.slice(ultimo) });
  return fatias;
}
